import type { ReactNode } from 'react';

/** Render common report markdown as React text; model output never becomes HTML. */
export default function SavedAnalysis({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.split(/\r?\n/);
  const inline = (line: string) => line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong> :
      /^`[^`]+`$/.test(part) ? <code key={i}>{part.slice(1, -1)}</code> : part);
  for (let i = 0; i < lines.length;) {
    const line = lines[i];
    const key = i;
    if (!line.trim()) { i++; continue; }
    if (/^```/.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[i++]);
      i++;
      blocks.push(<pre className="code-block" key={key}>{code.join('\n')}</pre>);
      continue;
    }
    const heading = line.match(/^#{1,6}\s+(.+)$/);
    if (heading) { blocks.push(<h5 key={key}>{inline(heading[1])}</h5>); i++; continue; }
    if (/^\s*(?:[-*]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]/.test(line);
      const pattern = ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/;
      const items: ReactNode[] = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push(<li key={i}>{inline(lines[i].replace(pattern, ''))}</li>); i++;
      }
      blocks.push(ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>);
      continue;
    }
    if (/^\s*---+\s*$/.test(line)) { blocks.push(<hr key={key}/>); i++; continue; }
    const paragraph: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(?:#|```|[-*]\s|\d+[.)]\s)/.test(lines[i])) {
      paragraph.push(lines[i++]);
    }
    blocks.push(<p key={key}>{inline(paragraph.join('\n'))}</p>);
  }
  return <div className="saved-analysis-text saved-analysis-rendered">{blocks}</div>;
}
